import { useEffect, type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { StateBlock } from '@w7/ui'
import { useCmMe } from '@/api/hooks/useCmMe'
import {
  clearPostLoginFalldown,
  consumePostLoginFalldown,
  logout,
} from '@/auth/oidc'
import { canAccessUiPath, firstAccessiblePath, isStaffCaller } from './staffAccess'

/** Staff deep-link / nav miss — show message then OIDC logout. */
function StaffUnauthorisedLogout() {
  useEffect(() => {
    logout({ reason: 'unauthorised' })
  }, [])
  return <StateBlock icon="info" title="Unauthorised Access" />
}

/** Blocks staff from routes their Access Level grants do not allow. */
export function GrantGate({ children }: { children: ReactNode }) {
  const location = useLocation()
  const me = useCmMe()

  if (me.isLoading) {
    return <StateBlock icon="activity" title="Checking access…" />
  }

  if (!canAccessUiPath(me.data?.caller, location.pathname)) {
    if (isStaffCaller(me.data?.caller)) {
      // Only the first landing after login falls down; later denials still logout.
      if (consumePostLoginFalldown()) {
        const to = firstAccessiblePath(me.data?.caller)
        if (to !== location.pathname && canAccessUiPath(me.data?.caller, to)) {
          return <Navigate to={to} replace />
        }
      }
      return <StaffUnauthorisedLogout />
    }
    return <Navigate to="/home" replace />
  }

  clearPostLoginFalldown()
  return <>{children}</>
}
